import { NAP } from '@/lib/public-content';

/*
 * Page metadata — ROADMAP.md §8.5: "SEO: titles, meta, Open Graph,
 * `LocalBusiness` JSON-LD with the real NAP."
 *
 * **The prototype has none of it** — no Open Graph tags and no JSON-LD at all
 * (public-site.md). Both are net new here, which is worth knowing because it
 * means there is nothing to port and nothing to match.
 *
 * No `react-helmet`. React 19 hoists `<title>`, `<meta>` and `<link>` out of any
 * component into `<head>` on its own, so a page states its own metadata beside
 * its own content and there is no second library deciding when that happens.
 */

export interface SeoProps {
  title: string;
  description: string;
  /** Path only. The origin comes from the browser — see below. */
  path: string;
}

export function Seo({ title, description, path }: SeoProps) {
  /*
   * The absolute URL is built from the live origin rather than from a
   * configured base.
   *
   * That matters right now: the app is served from a bare IP until cutover
   * (ROADMAP.md §1.2), so a hard-coded `https://honeycombcleaning.com` would
   * put canonical and Open Graph tags on the pre-cutover site pointing at a
   * domain that still serves the *old* site. Search engines would be told the
   * real page is somewhere else entirely.
   */
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  const url = `${origin}${path}`;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph — what a link to this page looks like pasted into
          Facebook, iMessage or a Slack channel. */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={NAP.name} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </>
  );
}

/**
 * `LocalBusiness` structured data, with the real NAP.
 *
 * **Rendered once, on the home page only.** Repeating it on every page would
 * describe the same business several times over, and a search engine reading
 * three copies has to guess which is canonical. One page, one description of
 * the business.
 *
 * `dangerouslySetInnerHTML` is the only way to emit a JSON-LD block — React
 * escapes text children, which would turn the quotes into entities and make the
 * script unparseable. The content is `JSON.stringify` of an object we built, so
 * there is no interpolation of anything a user typed. That is the property that
 * makes it safe, not the absence of the warning in the prop name.
 */
export function LocalBusinessJsonLd() {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'HouseCleaningService',
    name: NAP.legalName,
    alternateName: NAP.name,
    description:
      'Award-winning residential and commercial cleaning in Cedar City, Utah. Voted Best of Iron County 2023, 2024, 2025 and 2026.',
    telephone: NAP.phone,
    email: NAP.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: NAP.city,
      addressRegion: NAP.state,
      postalCode: NAP.postalCode,
      addressCountry: NAP.country,
    },
    areaServed: [
      { '@type': 'City', name: 'Cedar City' },
      { '@type': 'AdministrativeArea', name: 'Iron County' },
    ],
    // Monday through Saturday is the one part of the schedule the business has
    // actually published (business-rules.md §1). The *times* are a placeholder
    // in `setting` and are deliberately not asserted here — publishing opening
    // hours nobody confirmed would put a wrong time in a search result.
    openingHours: 'Mo-Sa',
    sameAs: [NAP.instagram],
    award: [
      'Best of Iron County 2023',
      'Best of Iron County 2024',
      'Best of Iron County 2025',
      'Best of Iron County 2026',
    ],
  };

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
}
